import * as React from 'react';
import isNil from 'lodash/isNil';

import Entity from 'components/entity/Entity';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import MetricsChannelsBasic from 'components/metrics/channels-basic/MetricsChannelsBasic';
import Popover from 'components/popover/Popover';
import Tooltip from 'components/Tooltip';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import { abbrNum } from 'utils/helper/common';
import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { prepareDataForMetricsChannelsBasic } from 'utils/metrics';
import { getEntityFromProfile } from 'utils/view';

applyTranslations(require('./onsite-performance-utils.i18n.json'));

import type {
  ChannelTypes,
  InfluencerCommon,
  PaginationLinks,
  PaginationMeta,
  SortableTableColumnType,
  SortableTableSort
} from 'types';
import type { OnsitePerformanceMetrics } from 'modules/reporting/types';

type MapByInfluencerId = {
  [x: number]: InfluencerCommon
};

type OnsiteDataWithMetrics = {
  onsiteMetrics: OnsitePerformanceMetrics
};

const DEFAULT_METRIC_WIDTH = 86;

const AVATAR_SIZE = {
  width: 24,
  height: 24
};

const POPOVER_AVATAR_SIZE = {
  width: 48,
  height: 48
};

const CREATOR_NAME_SIZE = {
  maxWidth: 132
};

const POPOVER_CREATOR_NAME_SIZE = {
  maxWidth: 154
};

const CHANNELS: Array<ChannelTypes> = ['instagram', 'tiktok', 'youtube'];

export const getAbbreviatedMetricValue = (value: string | number | null): string | number => {
  if (isNil(value)) {
    return 'n/a';
  }

  return abbrNum(value, { decimal: 1 }, true);
};

const getCreatorPopoverComponent = (creatorProfile: InfluencerCommon) => {
  const entity = getEntityFromProfile(creatorProfile);
  const entityUrl = `/content-creator/profiles/${entity?.id ?? ''}`;
  const metricsChannels = CHANNELS.map((channel: ChannelTypes) => prepareDataForMetricsChannelsBasic(creatorProfile, channel));

  return (
    <div className="subcampaigns-table__creator-popover">
      <EntityAvatar
        bordered
        customSize={POPOVER_AVATAR_SIZE}
        entity={entity}
        openInNewTab
      />
      <div className="subcampaigns-table__creator-popover-metrics">
        <EntityName
          className="subcampaigns-table__cell--creator"
          maxLength={CREATOR_NAME_SIZE.maxWidth}
          style={POPOVER_CREATOR_NAME_SIZE}
          size="small"
          entity={entity}
          link={entityUrl}
          openInNewTab
          hideTitle
        />
        <VerticalSpacing type="tiny" />
        <MetricsChannelsBasic
          includeRatio
          channels={metricsChannels}
          showChannels={['instagram', 'tiktok', 'youtube']}
          layout="block"
        />
      </div>
    </div>
  );
};

const getSortingFunction = (influencersMapById: MapByInfluencerId) => ({
  'creator-name': (a, b) => influencersMapById[a.influencerId]?.name?.localeCompare(influencersMapById[b.influencerId]?.name || ''),
  'link-clicks': (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.clicks - b.onsiteMetrics.clicks,
  'pdp-views': (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.pdpViews - b.onsiteMetrics.pdpViews,
  'wishlist-items': (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.itemsWishlist - b.onsiteMetrics.itemsWishlist,
  'basket-items': (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.itemsBasket - b.onsiteMetrics.itemsBasket,
  'sold-items': (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.itemsSold - b.onsiteMetrics.itemsSold,
  'gmv': (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.gmv - b.onsiteMetrics.gmv
});

export const getCreatorColumn = (influencersMapById: MapByInfluencerId): SortableTableColumnType => ({
  title: __t('onsite_performance_utils.column.influencer'),
  key: 'creator-name',
  width: 200,
  sort: (a, b) => influencersMapById[a.influencerId]?.name?.localeCompare(influencersMapById[b.influencerId]?.name || ''),
  render: (col) => {
    const entity = getEntityFromProfile(influencersMapById[col.influencerId]);
    const entityUrl = `/content-creator/profiles/${entity?.id ?? ''}`;

    return (
      <Popover
        contentType="mixed"
        triggerComponent={(
          <Entity>
            <EntityAvatar
              bordered
              customSize={AVATAR_SIZE}
              entity={entity}
              link={entityUrl}
              openInNewTab
            />
            <EntityName
              maxLength={CREATOR_NAME_SIZE.maxWidth}
              style={CREATOR_NAME_SIZE}
              size="small"
              entity={entity}
              link={entityUrl}
              openInNewTab
              hideTitle
            />
          </Entity>
        )}
      >
        {getCreatorPopoverComponent(influencersMapById[col.influencerId])}
      </Popover>
    );
  }
});

const getMetricTitle = (key: string): React.ReactNode => (
  <Tooltip
    width="normal"
    textAlign="center"
    message={__t(`onsite_performance_utils.popover.${key}`)}
  >
    {__t(`onsite_performance_utils.column.${key}`)}
  </Tooltip>
);

export const getMetricsColumns = (widths?: Array<number>): Array<SortableTableColumnType> => [
  {
    title: getMetricTitle('link_clicks'),
    key: 'link-clicks',
    width: widths?.[0] || DEFAULT_METRIC_WIDTH,
    align: 'center',
    sort: (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.clicks - b.onsiteMetrics.clicks,
    render: (col: OnsiteDataWithMetrics) => getAbbreviatedMetricValue(col.onsiteMetrics.clicks)
  },
  {
    title: getMetricTitle('pdp_views'),
    key: 'pdp-views',
    width: widths?.[1] || DEFAULT_METRIC_WIDTH,
    align: 'center',
    sort: (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.pdpViews - b.onsiteMetrics.pdpViews,
    render: (col: OnsiteDataWithMetrics) => getAbbreviatedMetricValue(col.onsiteMetrics.pdpViews)
  },
  {
    title: getMetricTitle('wishlist_items'),
    key: 'wishlist-items',
    width: widths?.[2] || DEFAULT_METRIC_WIDTH,
    align: 'center',
    sort: (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.itemsWishlist - b.onsiteMetrics.itemsWishlist,
    render: (col: OnsiteDataWithMetrics) => getAbbreviatedMetricValue(col.onsiteMetrics.itemsWishlist)
  },
  {
    title: getMetricTitle('basket_items'),
    key: 'basket-items',
    width: widths?.[3] || DEFAULT_METRIC_WIDTH,
    align: 'center',
    sort: (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.itemsBasket - b.onsiteMetrics.itemsBasket,
    render: (col: OnsiteDataWithMetrics) => getAbbreviatedMetricValue(col.onsiteMetrics.itemsBasket)
  },
  {
    title: getMetricTitle('sold_items'),
    key: 'sold-items',
    width: widths?.[4] || DEFAULT_METRIC_WIDTH,
    align: 'center',
    sort: (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.itemsSold - b.onsiteMetrics.itemsSold,
    render: (col: OnsiteDataWithMetrics) => getAbbreviatedMetricValue(col.onsiteMetrics.itemsSold)
  },
  {
    title: getMetricTitle('gmv'),
    key: 'gmv',
    width: widths?.[5] || DEFAULT_METRIC_WIDTH,
    align: 'center',
    sort: (a: OnsiteDataWithMetrics, b: OnsiteDataWithMetrics) => a.onsiteMetrics.gmv - b.onsiteMetrics.gmv,
    render: (col: OnsiteDataWithMetrics) => {
      if (isNil(col.onsiteMetrics.gmv)) {
        return 'n/a';
      }

      return renderPrice(col.onsiteMetrics.gmv, { abbreviate: { decimal: 1, allowDecimalPlaces: true } });
    }
  }
];

export function getSortedData<T>(
  data: Array<T>,
  influencersMapById: MapByInfluencerId,
  sortingOptions: SortableTableSort
): Array<T> {
  const sortBy = getSortingFunction(influencersMapById);

  return data.slice().sort((a, b) => {
    // https://lodash.com/docs/4.17.15#sortBy
    const order = sortBy[sortingOptions.sortingKey](a, b);
    return sortingOptions.ascending ? order : -order;
  });
}

export function dataProvider<T>({ page, limit, data }: {
  page: number,
  limit: number,
  data: Array<T>
}): {
  links: PaginationLinks,
  meta: PaginationMeta,
  data: Array<T>
} {
  const ITEMS_PER_PAGE = limit || Number.MAX_SAFE_INTEGER;

  const itemsTotal = data.length;
  const pagesTotal = Math.ceil(itemsTotal / ITEMS_PER_PAGE);
  const slice = data.slice(
    (page - 1) * ITEMS_PER_PAGE,
    Math.min(page * ITEMS_PER_PAGE, itemsTotal)
  );

  return {
    meta: {
      currentPage: page,
      pagesTotal,
      itemsTotal,
      itemsPerPage: ITEMS_PER_PAGE,
      itemsCurrentPage: slice.length
    },
    data: slice,
    links: {
      current: 'current',
      first: 'first',
      last: 'last',
      prev: page > 1 ? 'prev' : null,
      next: page < pagesTotal ? 'next' : null
    }
  };
}
